import "./CaseReport.css";

import classNames from "classnames";
import { DateTime } from "luxon";
import {
  ReactNode,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  Case,
  CaseReportSignature,
  CliaCertificate,
  SlideClassification,
  Stain,
  User,
  UserCredentials,
  LocationCode,
  CaseReport as OpenApiCaseReport,
  ScreenRequest,
  Clinic,
} from "pwapi";

import { BillCache } from "pwapi/models/BillCache";

import { CaseAddon, CaseDiagnosis } from "custom-models";

import { fetchSignee, patchCaseAddon } from "shared/actions/caseDetailActions";
import { fetchDisclaimers } from "shared/actions/disclaimerActions";
import { fetchUserSignatures } from "shared/actions/sysActions";
import { useIsLoading } from "shared/hooks/requestStatusHooks";
import { CaseDetailState } from "shared/reducers/caseDetailReducer";
import { SpecimenDetail } from "shared/reducers/specimenDetailReducer";
import { FeatureFlags } from "shared/reducers/sysReducer";
import {
  isAZDPLab,
  isBiopsyDiagnosticsCert,
  isConsultantsInLaboratoryMedicineCert,
  isDermDXCert,
  isPromedicaCert,
  isPropathCert,
  isPropathLab,
  isPSPCCert,
  isSouthTexasCert,
  isUFHealthCert,
} from "shared/utils/labUtils";
import { sortSpecimens, specimen_source } from "shared/utils/specimenUtils";

import { canAttachDocument } from "components/admin/documentsPanel/editDocumentsFormUtils";
import DocumentsPreviewPanel from "components/admin/editCasePanel/DocumentsPreviewPanel";
import { PathologyWatchButton } from "components/customInputs/buttons/Button";
import TextareaInput from "components/customInputs/TextareaInput";
import LogoLink from "components/header/LogoLink";
import {
  findScreenDisclaimer,
  getHighestVersionDiagnosis,
  isDiagnosable,
  isDigitizationCode,
  isModifyingReport,
} from "components/viewer/dialogs/utils";
import DocsListItem from "components/viewer/DocsListItem";
import useViewerDocuments from "hooks/useViewerDocuments";
import AZDPLogo from "images/azdp-logo.svg";
import BiospyDiagnosticsLogo from "images/biopsy-diagnostics-logo.svg";
import CleavelandSkinLogo from "images/cleveland-skin-pathology-logo.svg";
import CILMLogo from "images/consultants-in-laboratory-medicine-logo.svg";
import DermDXLogo from "images/dermdx-logo.svg";
import GlobalLogo from "images/global-pathology-logo.svg";
import LMCLogo from "images/lmc-pathology.svg";
import PWLogo from "images/logo.png";
import MAPSLogo from "images/maps-logo.svg";
import NETILogo from "images/neti-logo.svg";
import PServicesLogo from "images/pathology_services-logo.svg";
import PinkusLogo from "images/pinkus-logo.svg";
import PropathLogo from "images/propath-logo.svg";
import PropathPoweredByLogo from "images/propath-powered-by-pw-logo.svg";
import SHUSAPoweredByLogo from "images/shusa-powered-by-pw-logo.svg";
import TwinCitiesLogo from "images/twin-cities-pw.svg";
import ApplicationState from "store/ApplicationState";
import { useAppSelector } from "store/hooks";

import CaseReportHeader from "./CaseReportHeader";
import SignatureField, { SignatureLine } from "./SignatureField";
import {
  cptCodeStringFromChargeCaches,
  cptCodeStringFromDiagnoses,
  getLocationStringForSignature,
  getPhoneString,
  icd10StringFromDiagnoses,
  PW_DATETIME_WITH_ZONE,
  scannerTypeToManufacturer,
} from "./utils";

export type DisclaimersObject = {
  type: string;
  disclaimer: string;
};

type CaseReportProps = {
  disclaimersDryRun?: DisclaimersObject[];
  caseDetailObject: CaseDetailState;
  currentUser?: User;
  actions?: ReactNode;
  diagnoses: DiagnosesObject[];
  selectedSignature?: CaseReportSignature;
  addon?: string;
  useChangeAddon?: boolean;
  isUnsigned?: boolean;
  isPreview?: boolean;
  isPrelimReport?: boolean;
  isTechnicalReport?: boolean;
  features?: FeatureFlags;
  onChangeSignature?: Function;
  locationCode?: LocationCode;
  reportSerializationVersion?: number;
  report?: OpenApiCaseReport;
};

type DiagnosesObject = {
  specimen: SpecimenDetail;
  diagnosis?: CaseDiagnosis;
  screenDiagnosis?: CaseDiagnosis;
};

const getTCLabInfo = (cert?: CliaCertificate) => {
  // Dropping back down to cert?.lab?.location for backwards compatibility
  // TODO: remove this drop back down after cert.location becomes required
  const location = cert?.location || cert?.lab?.location || undefined;

  //TODO: Remove hardcoded Propath logic when removing all hardcoded lab functionality
  return (
    cert && (
      <>
        {cert.name}
        <br />
        {location?.street}
        {location?.street2 && <>, {location?.street2}</>}
        <br />
        {location?.city}
        {", "}
        {location?.state} {location?.zipcode}
        <br />
        {location?.phone1 && (
          <>
            Phone: {getPhoneString(location.phone1)}
            <br />
          </>
        )}
        {location?.fax && (
          <>
            Fax: {getPhoneString(location.fax)}
            <br />
          </>
        )}
        CLIA #: {cert.number}
        {isPSPCCert(cert) && (
          <>
            <br />
            Gross examination performed at: {cert.name}
          </>
        )}
      </>
    )
  );
};

const AddonItem = ({
  addon,
  timezone,
  orderNumber,
}: {
  addon: CaseAddon;
  timezone?: string | null;
  orderNumber: number;
}) => {
  return (
    <>
      <tr>
        <td className="AddonItem__header">
          {addon.addon_type === "amend" ? "Amendment" : "Addendum"}{" "}
          {orderNumber}
        </td>
      </tr>
      <tr>
        <td>{addon.content}</td>
      </tr>
      <tr>
        <td className="AddonItem__user">
          {addon.user?.first_name}&nbsp;
          {addon.user?.last_name} -&nbsp;
          {addon.signed_at
            ? DateTime.fromISO(addon.signed_at, {
                zone: timezone || "UTC",
              }).toLocaleString({
                ...PW_DATETIME_WITH_ZONE,
                timeZoneName: "long",
              })
            : "Preview"}
        </td>
      </tr>
    </>
  );
};

const HiddenSignatureRows = ({
  onChangeSignature,
  user,
  useCredentials = true,
}: {
  onChangeSignature?: Function;
  user?: UserCredentials;
  useCredentials?: boolean;
}) => (
  <>
    <tr className={onChangeSignature && "PrintReport__hiddenInfo"}>
      <td>
        {user?.first_name} {user?.last_name}
        {user?.userprofile?.credentials && useCredentials
          ? `, ${user.userprofile.credentials}`
          : ""}
      </td>
    </tr>
    {user?.userprofile?.title && useCredentials ? (
      <tr className={onChangeSignature && "PrintReport__hiddenInfo"}>
        <td>{user.userprofile.title}</td>
      </tr>
    ) : undefined}
  </>
);

const CaseReport = ({
  disclaimersDryRun,
  caseDetailObject,
  currentUser,
  actions,
  diagnoses,
  features,
  selectedSignature,
  onChangeSignature,
  useChangeAddon,
  isUnsigned,
  isPreview = true,
  isPrelimReport = false,
  isTechnicalReport = false,
  locationCode,
  reportSerializationVersion,
  report,
}: CaseReportProps) => {
  const dispatch = useDispatch();
  const isModifyingRep =
    features && isModifyingReport(caseDetailObject, features, currentUser);
  const signatures = useSelector(
    ({ userSignatures }: ApplicationState) =>
      userSignatures?.[
        isModifyingRep?.source === CaseDiagnosis.source.CORRECTION
          ? caseDetailObject.report_signed_signature?.user_id || 0
          : currentUser?.id || 0
      ]
  );
  const defaultSignature =
    signatures?.find(
      (s) => s.id === caseDetailObject.finalizing_signature_id
    ) || signatures?.[0];

  const isLoadingSignatures = useIsLoading(fetchUserSignatures, {
    userId: caseDetailObject.report_signed_signature?.user_id || 0,
  });

  const isLoadingSignee = useIsLoading(fetchSignee, {
    id: caseDetailObject.report_signed_signature?.user_id || 0,
  });

  const disclaimers = useSelector(
    ({ disclaimers }: ApplicationState) => disclaimers
  );

  const stains = useAppSelector(({ stains }) => stains);

  const { otherDocuments } = useViewerDocuments(caseDetailObject);
  const documents = otherDocuments.filter((doc) =>
    canAttachDocument(doc.category)
  );
  const [openDocuments, setOpenDocuments] = useState<boolean>(
    isPreview && !!features?.useMergeCaseReportWorkflow
  );
  const [openPreview, setOpenPreview] = useState<number | null>(null);
  const [screenDisclaimers, setScreenDisclaimers] = useState<
    DisclaimersObject | undefined
  >(undefined);
  // Due to an issue with print previews 'stretching' during a scroll, the footer falls off the print preview unless the user is at the top of the page.
  // This section of scroll code is to force the user to the top of the page, then return them to the portion of the page they were originally at when
  //  print previewed the page.
  const printScrollY = useRef<number>(0);

  // Set tfoot height dynamically to pad each page during print to match footer height
  // This is because the way the report has been set up with tables, the footer is
  // ... positioned "fixed", and the contents of the table will run under it. Guessing
  // ... at the right pixel height is not appropriate because it can cause page breaks
  // ... in weird ways (ref. email "Fwd: Incomplete path report", Rachel Reeg, 02/20/25)
  const [tfootHeight, settfootHeight] = useState(48);
  const footerRef = useRef<HTMLElement>(null);
  useLayoutEffect(() => {
    settfootHeight(
      (height) => footerRef.current?.getBoundingClientRect().height || height
    );
  }, []);

  const beforePrintScroll = () => {
    printScrollY.current = window.scrollY;
    window.scrollTo(0, 0);
  };

  const afterPrintScroll = () => {
    window.scrollTo(0, printScrollY.current);
  };

  // Push the user to the top of the page when print previewing to preserve the footers
  useEffect(() => {
    window.addEventListener("beforeprint", beforePrintScroll);
    return () => {
      window.removeEventListener("beforeprint", beforePrintScroll);
    };
  }, []);

  // Return the user back to where they were when the print previewed the page
  useEffect(() => {
    window.addEventListener("afterprint", afterPrintScroll);
    return () => {
      window.removeEventListener("afterprint", afterPrintScroll);
    };
  }, []);

  useEffect(() => {
    if (onChangeSignature && !selectedSignature && !!signatures?.length) {
      onChangeSignature(
        isModifyingRep?.source === CaseDiagnosis.source.CORRECTION &&
          caseDetailObject.report_signed_signature
          ? caseDetailObject.report_signed_signature
          : defaultSignature
      );
    }
  }, [
    onChangeSignature,
    selectedSignature,
    !!signatures?.length,
    defaultSignature,
  ]);

  // TODO: @James fix the deps in these `useEffect`s
  useEffect(() => {
    if (
      isModifyingRep?.source === CaseDiagnosis.source.CORRECTION &&
      caseDetailObject.report_signed_signature?.user_id &&
      !signatures &&
      !isLoadingSignatures
    ) {
      dispatch(
        fetchUserSignatures({
          userId: caseDetailObject.report_signed_signature.user_id,
        })
      );
    }
  }, [caseDetailObject, currentUser]); // FIXME: Missing `isModifyingRep?.source`, etc

  useEffect(() => {
    if (
      isModifyingRep?.source === CaseDiagnosis.source.CORRECTION &&
      caseDetailObject.report_signed_signature?.user_id &&
      !caseDetailObject.report_signee &&
      !isLoadingSignee
    ) {
      dispatch(
        fetchSignee({ id: caseDetailObject.report_signed_signature.user_id })
      );
    }
  }, [caseDetailObject, currentUser]); // FIXME: Missing `isModifyingRep?.source`, etc

  useEffect(() => {
    if (features?.useDatabaseDisclaimers && disclaimersDryRun) {
      setScreenDisclaimers(findScreenDisclaimer(disclaimersDryRun));
    } else if (caseDetailObject.disclaimers) {
      setScreenDisclaimers(findScreenDisclaimer(caseDetailObject.disclaimers));
    }
  }, [
    features?.useDatabaseDisclaimers,
    disclaimersDryRun,
    caseDetailObject.disclaimers,
  ]);

  const getAddonFromType = (type: string) =>
    caseDetailObject.case_addon
      ?.filter((f) => f.addon_type === type && f.signed_at)
      .sort((a, b) => ((b.signed_at || "") > (a.signed_at || "") ? 1 : -1));

  const affixMicroscopic = (
    diag: DiagnosesObject,
    screenRequests?: ScreenRequest[]
  ) => {
    const { diagnosis, screenDiagnosis, specimen } = diag;

    // Dry run for report previews ||
    // Serialized case object has for finalized reports generated by the lambda.
    // If either are true, append nothing to microscopic. It will show lower down
    // in the disclaimers section.
    if (
      (features?.useDatabaseDisclaimers && disclaimersDryRun) ||
      caseDetailObject.disclaimers
    ) {
      return diagnosis?.microscopic;
    }

    // Old logic for backwards compatibility on legacy reports
    if (
      diagnosis &&
      screenDiagnosis &&
      screenDiagnosis.user &&
      diagnosis.is_screen_valid
    ) {
      const screenDisclaimer = `Dr. ${screenDiagnosis.user.first_name} ${
        screenDiagnosis.user.last_name
      }, a board certified dermatopathologist, has reviewed ${
        screenRequests?.length ? `Part ${specimen.name}` : "the case"
      } and concurs.`;

      return diagnosis?.microscopic
        ? `${diagnosis?.microscopic}\n\n${screenDisclaimer}`
        : screenDisclaimer;
    }

    return diagnosis?.microscopic;
  };

  const unsignedAddon = caseDetailObject.case_addon?.find(
    (f) => !f.signed_at && f.user?.id === currentUser?.id
  );

  const amendments = useMemo(
    () => getAddonFromType("amend"),
    [caseDetailObject]
  );

  const addenda = useMemo(() => getAddonFromType("addend"), [caseDetailObject]);

  const onChangeAddon = (e: any) => {
    unsignedAddon?.id &&
      dispatch(
        patchCaseAddon(
          unsignedAddon.id,
          unsignedAddon.addon_type,
          e.target.value
        )
      );
  };

  // TODO: Verify the back end currently includes these in its /api/cases/{id}/ response
  const anyClassifications = caseDetailObject.slides.some(
    (slide) =>
      slide.classifications?.filter(
        (s) => s.status === SlideClassification.status.SUCCEEDED
      ).length
  );

  const shouldShowMihmDisclaimer =
    (isDiagnosable(caseDetailObject) &&
      features?.prospectiveAI &&
      anyClassifications) ||
    // Viewing user does not have the flag, but user who finalized the case did-
    // so the case was mihm assisted and we should add the disclaimer
    (!isDiagnosable(caseDetailObject) &&
      diagnoses.some((diag) => diag?.diagnosis?.is_mihm_assisted));

  const shouldShowIHCDisclaimer = caseDetailObject.slides?.some(
    (s) =>
      stains[s.stain_id || 0]?.category === Stain.category.SPEC ||
      stains[s.stain_id || 0]?.category === Stain.category.IHC ||
      stains[s.stain_id || 0]?.category === Stain.category.PW_SPEC ||
      stains[s.stain_id || 0]?.category === Stain.category.PW_IHC ||
      stains[s.stain_id || 0]?.category === Stain.category.CED_IHC ||
      stains[s.stain_id || 0]?.category === Stain.category.CED_SPEC
  );

  const shouldShowDigitizationDisclaimer =
    reportSerializationVersion && reportSerializationVersion < 12
      ? diagnoses
          .flatMap((diag) => diag.diagnosis?.cpt_codes)
          .some((cpt) => cpt && isDigitizationCode(cpt))
      : caseDetailObject.specimens?.some((s) => !s.glass_read);

  const scannerCompanies = [
    ...new Set(
      caseDetailObject.slides
        ?.map((s) => scannerTypeToManufacturer(s.scanner?.scanner_type, s.name))
        .filter((v) => v)
    ),
  ];

  const pcCliaDifferentFromTcClia =
    caseDetailObject?.grossed_by_cert?.number !==
    selectedSignature?.clia_certificate?.number;

  const dateReportedDiagnosis = getHighestVersionDiagnosis(
    diagnoses.map((d) => d.diagnosis) as CaseDiagnosis[]
  );

  useEffect(() => {
    // !disclaimer, if it already exists in state we don't need to fetch it again
    (shouldShowIHCDisclaimer ||
      shouldShowMihmDisclaimer ||
      shouldShowDigitizationDisclaimer) &&
      !disclaimers &&
      dispatch(fetchDisclaimers());
  }, [
    shouldShowIHCDisclaimer,
    shouldShowMihmDisclaimer,
    shouldShowDigitizationDisclaimer,
    disclaimers,
  ]);

  // Specific clinics to apply TCD to no matter the sonic practice
  const isScenic =
    caseDetailObject.patient?.clinic?.name === "Scenic Dermatology";
  const isSkinCareMT = caseDetailObject.patient?.clinic?.name === "SkinCare MT";

  // TODO: These should be replaced with taking the logo URL directly from Sonic Practice
  const isNETI =
    caseDetailObject.patient?.clinic?.sonic_practice?.acronym === "NETI";
  const isTwinCities =
    caseDetailObject.patient?.clinic?.sonic_practice?.acronym === "TCD" ||
    isScenic ||
    isSkinCareMT;
  const isPinkus =
    caseDetailObject.patient?.clinic?.sonic_practice?.acronym === "PDL";
  const isMAPS =
    caseDetailObject.patient?.clinic?.sonic_practice?.acronym === "MAPS";
  const isGlobal =
    caseDetailObject.patient?.clinic?.sonic_practice?.acronym === "GPL" &&
    caseDetailObject.patient?.clinic?.client_type !==
      Clinic.client_type.ARBITRAGE;
  const isAZDP =
    (caseDetailObject.patient?.clinic?.sonic_practice?.acronym === "AZD" &&
      isAZDPLab(caseDetailObject?.accessioning_lab) &&
      caseDetailObject.patient?.clinic?.client_type !==
        Clinic.client_type.ARBITRAGE) ||
    (isAZDPLab(caseDetailObject?.accessioning_lab) &&
      caseDetailObject.patient?.clinic?.sonic_practice?.acronym === "PPA");
  const isDermDX =
    isDermDXCert(selectedSignature?.clia_certificate) ||
    caseDetailObject.patient?.clinic?.sonic_practice?.acronym === "DNE";
  const isCleavelandSkin =
    caseDetailObject.patient?.clinic?.sonic_practice?.acronym === "CSPL";
  const isSouthTexas =
    (caseDetailObject.patient?.clinic?.sonic_practice?.acronym === "STDL" &&
      caseDetailObject.patient?.clinic?.client_type !==
        Clinic.client_type.ARBITRAGE) ||
    isSouthTexasCert(selectedSignature?.clia_certificate);
  const isTexasDermatology =
    caseDetailObject.patient?.clinic?.clinic_group?.name ===
    "Texas Dermatology";
  const isSanAntonioSkinCancerClinic =
    caseDetailObject.patient?.clinic?.name ===
    "San Antonio Skin & Cancer Clinic - Medical Center Tower 1";
  const isLMC =
    caseDetailObject.patient?.clinic?.sonic_practice?.acronym === "LMC";
  const isConsultantsInLaboratoryMedicine =
    isConsultantsInLaboratoryMedicineCert(selectedSignature?.clia_certificate);
  const isBiopsyDiagnostics = isBiopsyDiagnosticsCert(
    selectedSignature?.clia_certificate
  );

  const isTexasTech =
    isPropathLab(caseDetailObject?.accessioning_lab) &&
    caseDetailObject.patient?.clinic?.name ===
      "Texas Tech Physicians Dermatology";
  const isKeehan =
    caseDetailObject.patient?.clinic?.name === "Keehan Dermatology";
  const isKeehanPropath =
    isKeehan && isPropathCert(selectedSignature?.clia_certificate);
  const isKeehanSouthTexas =
    isKeehan && isSouthTexasCert(selectedSignature?.clia_certificate);

  const isPropath =
    caseDetailObject.patient?.clinic?.sonic_practice?.acronym === "PRO";
  const isPropathAPNewSales =
    isPropath &&
    caseDetailObject.patient?.clinic?.client_type === Clinic.client_type.AP;
  const isVanilla =
    caseDetailObject.patient?.clinic?.client_transition_type ===
    Clinic.client_transition_type.VANILLA;
  const isFullDigital =
    caseDetailObject.patient?.clinic?.client_transition_type ===
    Clinic.client_transition_type.FULL;
  const isPropathVanilla = isPropath && isVanilla;
  const isPropathFull = isPropath && isFullDigital;

  // TODO: In future "logo driven by Sonic Practice" code,
  // ... "PW" Sonic Practice still needs logic for PSPC
  const isPSPC =
    caseDetailObject.grossed_by_cert?.name.includes("Pathology Services") ||
    caseDetailObject.biller?.name === "PSPC";

  const isCosmeticDerm =
    caseDetailObject.patient?.clinic?.name ===
      "Cosmetic Dermatology & Aesthetic Laser Center" ||
    caseDetailObject.patient?.clinic?.external_clinic_id === "MA-ALK0";
  const isUFHealth = isUFHealthCert(selectedSignature?.clia_certificate);

  const hideBranding = isCosmeticDerm || isUFHealth;
  const branding = isKeehanPropath
    ? {
        logo: PropathPoweredByLogo,
        alt: "ProPath powered by PathologyWatch",
        minHeight: "80px",
      }
    : isKeehanSouthTexas
    ? {
        logo: SHUSAPoweredByLogo,
        alt: "Sonic Healthcare USA powered by PathologyWatch",
        minHeight: "80px",
      }
    : isConsultantsInLaboratoryMedicine // Noting that CILM needs to be before others
    ? {
        logo: CILMLogo,
        alt: "Consultants in Laboratory Medicine",
        minHeight: "68px",
      }
    : isBiopsyDiagnostics // Noting that Biopsy Diagnostics needs to be before others
    ? {
        logo: BiospyDiagnosticsLogo,
        alt: "Biopsy Diagnostics, LLC",
        minHeight: "68px",
      }
    : isTexasTech
    ? {
        logo: PropathLogo,
        alt: "ProPath",
        minHeight: "68px",
      }
    : isNETI
    ? {
        logo: NETILogo,
        alt: "New England Tissue Issue",
        minHeight: "68px",
      }
    : isPropathAPNewSales
    ? {
        logo: PropathLogo,
        alt: "ProPath",
        minHeight: "68px",
      }
    : isPropathVanilla
    ? {
        logo: PropathLogo,
        alt: "ProPath",
        minHeight: "68px",
      }
    : isPropathFull
    ? {
        logo: PropathPoweredByLogo,
        alt: "ProPath powered by PathologyWatch",
        minHeight: "80px",
      }
    : isTwinCities
    ? {
        logo: TwinCitiesLogo,
        alt: "Twin Cities Dermatopathology",
        minHeight: "68px",
      }
    : isPinkus
    ? {
        logo: PinkusLogo,
        alt: "Pinkus Dermatopathology Laboratory",
        minHeight: "68px",
      }
    : isMAPS
    ? {
        logo: MAPSLogo,
        alt: "Mid-Atlantic Pathology Services",
        minHeight: "68px",
      }
    : isGlobal
    ? {
        logo: GlobalLogo,
        alt: "Global Pathology",
        minHeight: "68px",
      }
    : isCleavelandSkin
    ? {
        logo: CleavelandSkinLogo,
        alt: "Cleaveland Skin Pathology",
        minHeight: "68px",
      }
    : isSouthTexas
    ? {
        logo: SHUSAPoweredByLogo,
        alt: "South Texas Dermatopathology",
        minHeight: "80px",
      }
    : isTexasDermatology || isSanAntonioSkinCancerClinic
    ? {
        logo: SHUSAPoweredByLogo,
        alt: "Sonic Healthcare USA powered by PathologyWatch",
        minHeight: "80px",
      }
    : isLMC
    ? {
        logo: LMCLogo,
        alt: "LMC Pathology Services",
        minHeight: "68px",
      }
    : isAZDP
    ? {
        logo: AZDPLogo,
        alt: "Arizona Dermatopathology",
        minHeight: "68px",
      }
    : isDermDX
    ? {
        logo: DermDXLogo,
        alt: "DermDX New England",
        minHeight: "68px",
      }
    : isPSPC
    ? {
        logo: PServicesLogo,
        alt: "Pathology Services",
        minHeight: "80px",
      }
    : {
        logo: PWLogo,
        alt: "PathologyWatch",
        minHeight: "80px",
      };

  const { REACT_APP_BACKEND_PROTOCOL, REACT_APP_BACKEND_HOST } = process.env;

  const urlPrefix = `${REACT_APP_BACKEND_PROTOCOL}://${
    REACT_APP_BACKEND_HOST
      ? REACT_APP_BACKEND_HOST.replace("api", "app")
      : "app.pathwatch.io"
  }/cases/`;

  const useAutoCptCodes =
    !!caseDetailObject?.bill_caches?.filter(
      (b) => b.generate_integration_bill && b.type !== BillCache.type.TEST
    )?.length &&
    !caseDetailObject?.billing_date &&
    !caseDetailObject?.has_biller_result_without_bill;

  return (
    <div className="PrintReportWrapper">
      <div className="ReportActionsBar ReportActionsHeader">
        {openPreview && (
          <DocumentsPreviewPanel
            documentId={openPreview}
            closePanel={() => setOpenPreview(null)}
          />
        )}
        <div className="ReportActionsBar__section ReportActionsBar--logo">
          <LogoLink goBack type="simple" />
          <span>Go back to {caseDetailObject.accession || "Case"}</span>
        </div>
        {features?.useMergeCaseReportWorkflow && !!documents.length && (
          <PathologyWatchButton
            onClick={() => setOpenDocuments(!openDocuments)}
          >
            <>
              Attached Documents (
              {documents?.filter((doc) => doc.attach_to_case_report).length})
            </>
          </PathologyWatchButton>
        )}
        {openDocuments && !!documents.length && (
          <div className="ReportActionsSidebar">
            <ul className="DocumentsSideBar">
              {documents.map((doc) => (
                <DocsListItem
                  caseDetail={caseDetailObject}
                  document={doc}
                  setOpenPreview={setOpenPreview}
                  key={doc.id}
                />
              ))}
            </ul>
          </div>
        )}
      </div>
      <div className="ReportActionsBar ReportActionsFooter">
        <div className="ReportActionsBar__section">{actions}</div>
      </div>
      <div
        className={classNames("PrintReport", {
          "PrintReport--Sonic":
            // TODO: Could be replaced with sonic_practice && (sonic_practice !== 'PW' || sonic_practice !== "DCM")
            isAZDP ||
            isDermDX ||
            isNETI ||
            isTwinCities ||
            isPinkus ||
            isMAPS ||
            isGlobal ||
            isConsultantsInLaboratoryMedicine ||
            isBiopsyDiagnostics ||
            isTexasTech ||
            isCleavelandSkin ||
            isSouthTexas ||
            isTexasDermatology ||
            isSanAntonioSkinCancerClinic ||
            isLMC ||
            isPropathAPNewSales ||
            isPropathVanilla ||
            isPropathFull ||
            isKeehan,
        })}
      >
        <table>
          <thead>
            <tr>
              <td>
                <div
                  className={classNames("ReportHeader__logoSection", {
                    "ReportHeader__logoSection--blankLogo": hideBranding,
                  })}
                >
                  {!hideBranding && (
                    <img
                      className="brand-img"
                      src={branding.logo}
                      alt={branding.alt}
                      height={branding.minHeight}
                    />
                  )}
                  <h1>
                    {isPreview ? (
                      isTechnicalReport ? (
                        "Preview Technical Report"
                      ) : (
                        "PREVIEW REPORT"
                      )
                    ) : (
                      <>
                        {!isPrelimReport && (
                          <>
                            {isTechnicalReport
                              ? "Technical Report"
                              : "DERMATOPATHOLOGY REPORT"}
                            <br />
                          </>
                        )}
                        {!(caseDetailObject.glass_read || isVanilla) && (
                          <a href={`${urlPrefix}${caseDetailObject.accession}`}>
                            Secure Link to Digital Slide Display
                          </a>
                        )}
                      </>
                    )}
                  </h1>
                </div>
              </td>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="PrintReport__td--removePadding">
                <table>
                  <thead>
                    {isPrelimReport && (
                      <tr>
                        <td>
                          <div className="ReportHeader__prelimNote">
                            Preliminary Pathology Report
                          </div>
                        </td>
                      </tr>
                    )}
                    <tr>
                      <td>
                        <CaseReportHeader
                          caseDetailObject={caseDetailObject}
                          isPrelimReport={isPrelimReport}
                          dateReported={
                            dateReportedDiagnosis?.report_signed
                              ? {
                                  timestamp:
                                    dateReportedDiagnosis?.report_signed,
                                  timezone:
                                    dateReportedDiagnosis?.report_signed_timezone,
                                }
                              : report?.created_at
                              ? {
                                  timestamp: report?.created_at,
                                  timezone: report?.timezone,
                                }
                              : undefined
                          }
                          isUnsigned={isUnsigned}
                          selectedSignature={selectedSignature}
                        />
                      </td>
                    </tr>
                  </thead>
                  <tbody
                    className="PrintReport__table--mainBody"
                    // Important: This id is required for the automated lambda to generate a pdf, without it the lambda will not be able to understand the page has loaded
                    id={!isPreview ? "main__report__body" : undefined}
                  >
                    <tr>
                      <td>
                        {useChangeAddon && (
                          <div className="PrintReportSignature__selectBox">
                            <TextareaInput
                              label="Enter text for Amendment or Addendum below"
                              value={unsignedAddon?.content}
                              onChange={onChangeAddon}
                            />
                          </div>
                        )}
                        {((amendments && amendments.length > 0) ||
                          (useChangeAddon &&
                            unsignedAddon?.addon_type === "amend")) && (
                          <section className="PrintReportSection">
                            <h2 className="PrintReportSection__header">
                              Amendments
                            </h2>
                            <table>
                              <tbody className="AddonItem">
                                {unsignedAddon?.addon_type === "amend" && (
                                  <AddonItem
                                    addon={unsignedAddon}
                                    timezone={
                                      selectedSignature?.clia_certificate
                                        ?.location?.timezone
                                    }
                                    orderNumber={(amendments?.length || 0) + 1}
                                  />
                                )}
                                {amendments &&
                                  amendments.map((amend, index) => {
                                    return (
                                      <AddonItem
                                        addon={amend}
                                        orderNumber={amendments.length - index}
                                        timezone={
                                          dateReportedDiagnosis
                                            ? dateReportedDiagnosis.report_signed_timezone
                                            : selectedSignature
                                                ?.clia_certificate?.location
                                                ?.timezone
                                        }
                                        key={index}
                                      />
                                    );
                                  })}
                              </tbody>
                            </table>
                          </section>
                        )}
                        {((addenda && addenda.length > 0) ||
                          (useChangeAddon &&
                            unsignedAddon?.addon_type === "addend")) && (
                          <section className="PrintReportSection">
                            <h2 className="PrintReportSection__header">
                              Addenda
                            </h2>
                            <table>
                              <tbody className="AddonItem">
                                {unsignedAddon?.addon_type === "addend" && (
                                  <AddonItem
                                    addon={unsignedAddon}
                                    timezone={
                                      selectedSignature?.location?.timezone
                                    }
                                    orderNumber={(addenda?.length || 0) + 1}
                                  />
                                )}
                                {addenda &&
                                  addenda.map((addend, index) => (
                                    <AddonItem
                                      addon={addend}
                                      orderNumber={addenda.length - index}
                                      timezone={
                                        dateReportedDiagnosis
                                          ? dateReportedDiagnosis.report_signed_timezone
                                          : selectedSignature?.location
                                              ?.timezone
                                      }
                                      key={index}
                                    />
                                  ))}
                              </tbody>
                            </table>
                          </section>
                        )}
                        {!isTechnicalReport && (
                          <section className="PrintReportSection">
                            <h2 className="PrintReportSection__header">
                              {isPrelimReport ? "Preliminary" : "Final"}{" "}
                              Diagnosis:
                            </h2>
                            <table className="PrintReportSection__table">
                              <tbody className="PrintReport__diagnosis">
                                {diagnoses.map((diag, index) => (
                                  <tr key={index}>
                                    <td>{diag.specimen.name}.</td>
                                    <td>
                                      <span className="PrintReport__diagnosis--diagnosis">
                                        {caseDetailObject.accessioning_lab
                                          ?.settings?.uppercase_report_diagnosis
                                          ?.active
                                          ? specimen_source(
                                              diag.specimen
                                            )?.toUpperCase()
                                          : specimen_source(diag.specimen)}
                                        :
                                        <br />
                                        {(caseDetailObject.accessioning_lab
                                          ?.settings?.uppercase_report_diagnosis
                                          ?.active
                                          ? diag.diagnosis?.diagnosis?.toUpperCase()
                                          : diag.diagnosis?.diagnosis) ||
                                          (isPrelimReport &&
                                            "NO PRELIMINARY INFORMATION AVAILABLE")}
                                        {diag.diagnosis?.icd10_code && (
                                          <> ({diag.diagnosis?.icd10_code})</>
                                        )}
                                      </span>
                                      {diag.diagnosis?.comment && (
                                        <>
                                          <br />
                                          <br />
                                          <span className="PrintReport__diagnosis--note">
                                            NOTE: {diag.diagnosis.comment}
                                          </span>
                                        </>
                                      )}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </section>
                        )}
                        <section className="PrintReportSection">
                          <h2 className="PrintReportSection__header">
                            Clinical Information:
                          </h2>
                          <table className="PrintReportSection__table">
                            <tbody>
                              {diagnoses.map(
                                (diag, index) =>
                                  diag.specimen && (
                                    <tr key={index}>
                                      <td>{diag.specimen.name}.</td>
                                      {diag.specimen.clinical_history && (
                                        <td>
                                          {diag.specimen.clinical_history.toUpperCase()}
                                        </td>
                                      )}
                                    </tr>
                                  )
                              )}
                            </tbody>
                          </table>
                        </section>
                        {!isTechnicalReport && (
                          <section className="PrintReportSection">
                            <h2 className="PrintReportSection__header">
                              Microscopic Examination:
                            </h2>
                            <table className="PrintReportSection__table">
                              <tbody>
                                {diagnoses.map((diag, index) => (
                                  <tr key={index}>
                                    <td>{diag.specimen.name}.</td>
                                    <td>
                                      {isPrelimReport
                                        ? diag.diagnosis?.microscopic ||
                                          "No microscopic information available"
                                        : affixMicroscopic(
                                            diag,
                                            caseDetailObject.screen_requests
                                          )}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                            {shouldShowMihmDisclaimer &&
                              disclaimers?.mihm_disclaimer && (
                                <span
                                  key="mihmDisclaimer"
                                  className="PrintReport__disclaimer"
                                >
                                  {disclaimers.mihm_disclaimer}
                                </span>
                              )}
                            {shouldShowIHCDisclaimer &&
                              disclaimers?.ihc_disclaimer && (
                                <span
                                  key="ihcDisclaimer"
                                  className="PrintReport__disclaimer"
                                >
                                  {disclaimers?.ihc_disclaimer}
                                </span>
                              )}
                            {shouldShowDigitizationDisclaimer &&
                              disclaimers?.digitization_disclaimer && (
                                <span
                                  key="digitizationDisclaimer"
                                  className="PrintReport__disclaimer"
                                >
                                  {disclaimers?.digitization_disclaimer}
                                </span>
                              )}
                            {screenDisclaimers && (
                              <span
                                key="screenDisclaimer"
                                className="PrintReport__disclaimer"
                              >
                                {screenDisclaimers?.disclaimer}
                              </span>
                            )}
                          </section>
                        )}
                        <section className="PrintReportSection">
                          <h2 className="PrintReportSection__header">
                            Gross Description:
                          </h2>
                          <table className="PrintReportSection__table">
                            {isTechnicalReport ? (
                              <tbody>
                                {sortSpecimens(caseDetailObject?.specimens).map(
                                  (s) => (
                                    <tr key={s.id}>
                                      <td>{s.name}.</td>
                                      <td>{s.gross_description}</td>
                                    </tr>
                                  )
                                )}
                              </tbody>
                            ) : (
                              <tbody>
                                {diagnoses.map((diag, index) => (
                                  <tr key={index}>
                                    <td>{diag.specimen.name}.</td>
                                    <td>{diag.specimen.gross_description}</td>
                                  </tr>
                                ))}
                              </tbody>
                            )}
                          </table>
                        </section>
                        {!isTechnicalReport && (
                          <section style={{ padding: "0.5em 0 1em 0" }}>
                            <h2 className="PrintReportSection__header">
                              {`${
                                isPrelimReport ? "ISSUING" : "FINALIZING"
                              } PHYSICIAN`}
                            </h2>
                            <table
                              className="PrintReportSection__table"
                              style={{ width: "100%" }}
                            >
                              <tbody>
                                {caseDetailObject.source === Case.source.PW &&
                                !caseDetailObject.is_screening &&
                                !isPrelimReport ? (
                                  <>
                                    {onChangeSignature && signatures && (
                                      <tr>
                                        <td className="PrintReportSignature__selectBox">
                                          <SignatureField
                                            signatures={signatures}
                                            user={
                                              isModifyingRep?.source ===
                                                CaseDiagnosis.source
                                                  .CORRECTION &&
                                              caseDetailObject.report_signee
                                                ? caseDetailObject.report_signee
                                                : selectedSignature?.user
                                            }
                                            value={selectedSignature}
                                            onSelect={(newValue: any) => {
                                              onChangeSignature(newValue.label);
                                            }}
                                            locationCode={locationCode}
                                            pcCliaDifferentFromTcClia={
                                              pcCliaDifferentFromTcClia
                                            }
                                            shouldShowDigitizationDisclaimer={
                                              shouldShowDigitizationDisclaimer
                                            }
                                            scannerCompanies={scannerCompanies}
                                            labSettings={
                                              caseDetailObject?.accessioning_lab
                                                ?.settings
                                            }
                                          />
                                        </td>
                                      </tr>
                                    )}
                                    {selectedSignature && (
                                      <>
                                        <HiddenSignatureRows
                                          user={
                                            isModifyingRep?.source ===
                                            CaseDiagnosis.source.CORRECTION
                                              ? caseDetailObject.report_signee
                                              : selectedSignature?.user
                                          }
                                          onChangeSignature={onChangeSignature}
                                        />
                                        {dateReportedDiagnosis?.report_signed && (
                                          <tr
                                            className={
                                              onChangeSignature &&
                                              "PrintReport__hiddenInfo"
                                            }
                                          >
                                            <td>
                                              Final report electronically signed
                                              on{" "}
                                              {DateTime.fromISO(
                                                dateReportedDiagnosis.report_signed,
                                                {
                                                  zone: dateReportedDiagnosis?.report_signed_timezone,
                                                }
                                              ).toLocaleString({
                                                ...PW_DATETIME_WITH_ZONE,
                                                timeZoneName: "long",
                                              })}
                                            </td>
                                          </tr>
                                        )}

                                        {(reportSerializationVersion &&
                                          reportSerializationVersion < 20) ||
                                        !caseDetailObject?.accessioning_lab
                                          ?.settings?.new_signature_format ? (
                                          <>
                                            {/* This is the "old" signature, left as-is for existing serializations */}
                                            <tr
                                              className={
                                                onChangeSignature &&
                                                "PrintReport__hiddenInfo"
                                              }
                                            >
                                              <td>
                                                The professional component was
                                                performed at:{" "}
                                                {!isPromedicaCert(
                                                  selectedSignature.clia_certificate
                                                ) && (
                                                  <>
                                                    {
                                                      selectedSignature
                                                        .clia_certificate.name
                                                    }
                                                    {!selectedSignature
                                                      .clia_certificate
                                                      .suppress_location && (
                                                      <>, </>
                                                    )}
                                                  </>
                                                )}
                                                {!selectedSignature
                                                  .clia_certificate
                                                  .suppress_location &&
                                                  getLocationStringForSignature(
                                                    selectedSignature
                                                  )}
                                              </td>
                                            </tr>
                                            <tr
                                              className={
                                                onChangeSignature &&
                                                "PrintReport__hiddenInfo"
                                              }
                                            >
                                              <td>
                                                CLIA #:{" "}
                                                {
                                                  selectedSignature
                                                    .clia_certificate?.number
                                                }
                                                {locationCode && (
                                                  <> ; {locationCode.code}</>
                                                )}
                                              </td>
                                            </tr>
                                          </>
                                        ) : (
                                          <>
                                            <tr
                                              className={
                                                onChangeSignature &&
                                                "PrintReport__hiddenInfo"
                                              }
                                            >
                                              <td>
                                                <SignatureLine
                                                  selectedSignature={
                                                    selectedSignature
                                                  }
                                                  locationCode={locationCode}
                                                  pcCliaDifferentFromTcClia={
                                                    pcCliaDifferentFromTcClia
                                                  }
                                                  shouldShowDigitizationDisclaimer={
                                                    shouldShowDigitizationDisclaimer
                                                  }
                                                  scannerCompanies={
                                                    scannerCompanies
                                                  }
                                                />{" "}
                                                CLIA&nbsp;#&nbsp;
                                                {
                                                  selectedSignature
                                                    .clia_certificate?.number
                                                }
                                              </td>
                                            </tr>
                                          </>
                                        )}
                                      </>
                                    )}
                                  </>
                                ) : (
                                  !caseDetailObject.is_screening && (
                                    <HiddenSignatureRows
                                      user={caseDetailObject.active_assignment}
                                      useCredentials={isPrelimReport}
                                    />
                                  )
                                )}
                                {isPrelimReport && (
                                  <tr>
                                    <td>
                                      Preliminary report electronically issued
                                      on{" "}
                                      {(report?.created_at
                                        ? DateTime.fromISO(report.created_at)
                                        : DateTime.now()
                                      ).toLocaleString({
                                        ...PW_DATETIME_WITH_ZONE,
                                        timeZone:
                                          selectedSignature?.clia_certificate
                                            ?.location?.timezone || "UTC",
                                        timeZoneName: "long",
                                      })}
                                    </td>
                                  </tr>
                                )}
                                {(isUnsigned ||
                                  caseDetailObject.is_screening ||
                                  caseDetailObject.status ===
                                    Case.status.LIS_PENDING) &&
                                  !isPrelimReport && (
                                    <tr className="Preview__note">
                                      <td>
                                        {caseDetailObject.status ===
                                        Case.status.LIS_PENDING
                                          ? "Sent to LIS"
                                          : caseDetailObject.is_screening
                                          ? "Screening, Not Signing"
                                          : "Preview, Not Signed"}
                                      </td>
                                    </tr>
                                  )}
                              </tbody>
                            </table>
                          </section>
                        )}
                        {!isPrelimReport && (
                          <section
                            style={
                              isTechnicalReport
                                ? { paddingTop: ".5em" }
                                : undefined
                            }
                          >
                            <table className="PrintReportSection__table PrintReportSection__table--fixed PrintReportSection__table--preventBreak">
                              <tbody>
                                <tr>
                                  {caseDetailObject.source === Case.source.PW &&
                                  caseDetailObject.grossed_by_cert ? (
                                    <td
                                      className="PrintReport__td--removeVerticalPadding"
                                      data-testid="tcclia"
                                    >
                                      TC LABORATORY
                                      <br />
                                      {getTCLabInfo(
                                        caseDetailObject.grossed_by_cert
                                      )}
                                    </td>
                                  ) : (
                                    <td className="PrintReport__td--removeVerticalPadding">
                                      TC LABORATORY
                                    </td>
                                  )}
                                  {isTechnicalReport ? (
                                    <td
                                      className="PrintReport__td--removeVerticalPadding"
                                      data-testid="billingcodes"
                                    >
                                      BILLING CODES:
                                      <br />
                                      ICD10:{" "}
                                      {[
                                        ...new Set(
                                          sortSpecimens(
                                            caseDetailObject.specimens
                                          )
                                            .flatMap((s) => s.icd10codes)
                                            .map((icd) => icd?.code)
                                            .filter((v) => v)
                                        ),
                                      ].join(", ")}
                                      <br />
                                      CPT:{" "}
                                      {cptCodeStringFromChargeCaches(
                                        caseDetailObject.charge_caches || []
                                      )}
                                    </td>
                                  ) : (
                                    <td
                                      className="PrintReport__td--removeVerticalPadding"
                                      data-testid="billingcodes"
                                    >
                                      BILLING CODES:
                                      <br />
                                      ICD10:{" "}
                                      {icd10StringFromDiagnoses(
                                        diagnoses.reduce(
                                          (acc: CaseDiagnosis[], diag) => {
                                            return [
                                              ...acc,
                                              ...(diag.diagnosis
                                                ? [diag.diagnosis]
                                                : []),
                                            ];
                                          },
                                          []
                                        )
                                      )}
                                      <br />
                                      CPT:{" "}
                                      {(!reportSerializationVersion || // Preview
                                        (reportSerializationVersion &&
                                          reportSerializationVersion >= 24)) &&
                                      useAutoCptCodes
                                        ? cptCodeStringFromChargeCaches(
                                            caseDetailObject.charge_caches || []
                                          )
                                        : cptCodeStringFromDiagnoses(
                                            diagnoses.reduce(
                                              (acc: CaseDiagnosis[], diag) => {
                                                return [
                                                  ...acc,
                                                  ...(diag.diagnosis
                                                    ? [diag.diagnosis]
                                                    : []),
                                                ];
                                              },
                                              []
                                            )
                                          )}
                                    </td>
                                  )}
                                  <td className="PrintReport__td--removeVerticalPadding">
                                    EHR ROUTING IDENTIFIERS:
                                    <br />
                                    Encounter ID:{" "}
                                    {caseDetailObject.external_ezderm_order_id ||
                                      caseDetailObject.external_order_id}
                                    <br />
                                    Patient ID:{" "}
                                    {
                                      caseDetailObject.patient
                                        ?.medical_record_number
                                    }
                                    {caseDetailObject.patient?.clinic
                                      ?.external_clinic_id && (
                                      <>
                                        <br />
                                        Clinic Account Number:{" "}
                                        {
                                          caseDetailObject.patient?.clinic
                                            ?.external_clinic_id
                                        }
                                      </>
                                    )}
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                          </section>
                        )}
                      </td>
                    </tr>
                  </tbody>
                  <tfoot className="PrintReport__hiddenInfo">
                    <tr>
                      <td>
                        <div
                          style={{
                            height: `${tfootHeight}px`,
                          }}
                        >
                          &nbsp;
                        </div>
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </td>
            </tr>
          </tbody>
        </table>

        <footer className="PrintReport__footer" ref={footerRef}>
          {isPropathVanilla || isPropathFull ? (
            <span>
              Phone: (214) 638-2000 | Fax: (800) 258-1253 | ProPath.com
            </span>
          ) : (
            <span>
              Phone: (877) 463-5434 | Fax: (336) 360-0086 | PathologyWatch.com
            </span>
          )}
          {!(caseDetailObject.glass_read || isVanilla) && (
            <span>
              Link to Digital Slide Display:{" "}
              <a href={`${urlPrefix}${caseDetailObject.accession}`}>
                {urlPrefix}
                {caseDetailObject.accession}
              </a>
            </span>
          )}
        </footer>
        {isPrelimReport && (
          <div className="PrintReport__watermark">Preliminary</div>
        )}
      </div>
    </div>
  );
};

export default CaseReport;
